if (!customElements.get("gift-modal")) {
  class GiftModal extends ModalDialog {
    constructor() {
      super();
      this.cart = document.querySelector("cart-notification") || document.querySelector("cart-drawer");
      this._autoOpenedInDrawerSession = false;
      this._autoOpenFetching = false;
      this._dismissedByUser = false;
      this._suppressAutoOpenUntilClose = false;
      this._lastFreeGiftInCart = null;

      if (Shopify.designMode) {
        document.addEventListener("shopify:section:select", (e) => {
          if (e.detail.sectionId !== this.dataset.section) return;
          this.show();
        });
        document.addEventListener("shopify:section:deselect", (e) => {
          if (e.detail.sectionId !== this.dataset.section) return;
          this.hide();
        });
      }
    }
    #unsub = null;

    connectedCallback() {
      super.connectedCallback();
      // Prevent duplicate cartUpdate events fired in the same tick
      this._debounceTimer = null;

      this._closeButton = this.querySelector('[id^="ModalClose-"]');
      this._userDismissHandler = this.handleUserDismiss.bind(this);
      this._userDismissKeyHandler = this.handleUserDismissKey.bind(this);
      this._userDismissClickHandler = this.handleUserDismissClick.bind(this);
      this._closeButton?.addEventListener("click", this._userDismissHandler);
      this.addEventListener("keyup", this._userDismissKeyHandler);
      this.addEventListener("click", this._userDismissClickHandler);

      this._cartDrawerOpenHandler = this.handleCartDrawerOpen.bind(this);
      this._cartDrawerCloseHandler = this.handleCartDrawerClose.bind(this);
      document.addEventListener("cart-drawer:open", this._cartDrawerOpenHandler);
      document.addEventListener("cart-drawer:close", this._cartDrawerCloseHandler);

      this.#unsub = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
        clearTimeout(this._debounceTimer);
        this._debounceTimer = setTimeout(() => {
          // Always check and remove gift if needed
          if (event.source === "cart-items") {
            this.checkAndRemoveGift(event);
          } else {
            this.hide();
          }
          // Always run threshold logic for auto-open
          this.maybeAutoOpenFromCartUpdate(event?.cartData);
          this.scheduleAutoOpenCheckAfterUpdate();
        }, 0);
      });

      // MutationObserver to watch for quantity changes (plus/minus or input)
      this._quantityObserver = new MutationObserver(() => {
        // Debounce to avoid rapid firing
        clearTimeout(this._quantityObserverTimer);
        this._quantityObserverTimer = setTimeout(() => {
          this.fetchCartData().then((cartData) => {
            this.maybeAutoOpenFromCartUpdate(cartData);
          });
        }, 50);
      });

      // Observe changes in cart drawer (if present)
      const cartDrawer = document.querySelector("cart-drawer");
      if (cartDrawer) {
        const target = cartDrawer.querySelector("[data-quantity-selector]") || cartDrawer;
        this._quantityObserver.observe(target, { childList: true, subtree: true, attributes: true });
      }
    }

    get goodieRequirement() {
      return Number(this.dataset.goodieRequirement);
    }

    get autoOpenEnabled() {
      return this.dataset.autoOpen === "true";
    }


    get autoOpenCooldownSeconds() {
      const seconds = Number(this.dataset.autoOpenCooldownSeconds);
      return Number.isNaN(seconds) ? 0 : seconds;
    }

    get sessionDismissedKey() {
      return "goodie-auto-open-dismissed";
    }

    get sessionAutoOpenTsKey() {
      return "goodie-auto-open-last-ts";
    }

    getSessionStorage() {
      try {
        return window.sessionStorage;
      } catch (error) {
        return null;
      }
    }

    getSessionValue(key) {
      const storage = this.getSessionStorage();
      if (!storage) return null;
      return storage.getItem(key);
    }

    setSessionValue(key, value) {
      const storage = this.getSessionStorage();
      if (!storage) return;
      storage.setItem(key, value);
    }

    removeSessionValue(key) {
      const storage = this.getSessionStorage();
      if (!storage) return;
      storage.removeItem(key);
    }

    isDismissedInSession() {
      return this.getSessionValue(this.sessionDismissedKey) === "true";
    }

    getLastAutoOpenTs() {
      const ts = Number(this.getSessionValue(this.sessionAutoOpenTsKey));
      return Number.isNaN(ts) ? 0 : ts;
    }

    setLastAutoOpenTs(value) {
      this.setSessionValue(this.sessionAutoOpenTsKey, String(value));
    }

    markDismissedInSession() {
      this.setSessionValue(this.sessionDismissedKey, "true");
    }

    isCartDrawerOpen() {
      return document.querySelector("cart-drawer")?.classList.contains("active") === true;
    }

    handleCartDrawerOpen() {
      if (!this.autoOpenEnabled || this._autoOpenedInDrawerSession || this._autoOpenFetching) return;
      if (!this.isCartDrawerOpen()) return;

      this._drawerOpenedAt = Date.now();
      this._thresholdCrossedSinceOpen = false;
      this._autoOpenFetching = true;
      this.fetchCartData()
        .then((cartData) => this.maybeAutoOpenFromCartUpdate(cartData))
        .finally(() => {
          this._autoOpenFetching = false;
        });
    }

    scheduleAutoOpenCheckAfterUpdate() {
      if (!this.autoOpenEnabled || this._autoOpenedInDrawerSession) return;
      if (!this.isCartDrawerOpen()) return;
      if (this._autoOpenFetching) return;

      clearTimeout(this._autoOpenUpdateTimer);
      this._autoOpenUpdateTimer = setTimeout(() => {
        if (!this.autoOpenEnabled || this._autoOpenedInDrawerSession) return;
        if (!this.isCartDrawerOpen()) return;
        this._autoOpenFetching = true;
        this.fetchCartData()
          .then((cartData) => this.maybeAutoOpenFromCartUpdate(cartData))
          .finally(() => {
            this._autoOpenFetching = false;
          });
      }, 50);
    }

    handleCartDrawerClose() {
      this._autoOpenedInDrawerSession = false;
      this._suppressAutoOpenUntilClose = false;
      this._drawerOpenedAt = null;
      this._thresholdCrossedSinceOpen = false;
      this._lastCartTotal = null;
    }

    handleUserDismiss() {
      this._dismissedByUser = true;
    }

    handleUserDismissKey(event) {
      if (event.code?.toUpperCase() !== "ESCAPE") return;
      this._dismissedByUser = true;
    }

    handleUserDismissClick(event) {
      if (event.target !== this) return;
      this._dismissedByUser = true;
    }

    show(opener) {
      super.show(opener);
    }

    hide() {
      const wasOpen = this.hasAttribute("open");
      const dismissedByUser = this._dismissedByUser;
      this._dismissedByUser = false;
      super.hide();
      if (wasOpen && dismissedByUser) {
        this.markDismissedInSession();
        this._autoOpenedInDrawerSession = true;
      }
      this.restoreCartDrawerState();
    }

    restoreCartDrawerState() {
      const cartDrawer = document.querySelector("cart-drawer");
      if (!cartDrawer?.classList.contains("active")) return;

      document.body.classList.add("overflow-hidden");
      if (typeof trapFocus !== "function") return;

      const containerToTrapFocusOn = cartDrawer.classList.contains("is-empty")
        ? cartDrawer.querySelector(".drawer__inner-empty")
        : document.getElementById("CartDrawer");
      const focusElement = cartDrawer.querySelector(".drawer__inner") || cartDrawer.querySelector(".drawer__close");
      if (containerToTrapFocusOn && focusElement) {
        trapFocus(containerToTrapFocusOn, focusElement);
      }
    }

    async fetchCartData() {
      try {
        const response = await fetch(`${window.routes.cart_url}.js`);
        if (!response.ok) return null;
        return await response.json();
      } catch (error) {
        return null;
      }
    }

    maybeAutoOpenFromCartUpdate(cartData) {
      if (!this.autoOpenEnabled || this._autoOpenedInDrawerSession || this.hasAttribute("open")) {
        return;
      }
      if (!this.isCartDrawerOpen()) {
        return;
      }
      if (!cartData?.items || Number.isNaN(this.goodieRequirement)) {
        return;
      }

      const previousTotal = this._lastCartTotal;
      this._lastCartTotal = cartData.total_price;

      // Always check if threshold is crossed (even if previousTotal is null)
      let thresholdJustCrossed = false;
      if (
        previousTotal != null &&
        previousTotal < this.goodieRequirement &&
        cartData.total_price >= this.goodieRequirement
      ) {
        thresholdJustCrossed = true;
      }
      // If previousTotal is null (first load), treat as not crossing

      // If threshold was just crossed, set the flag
      if (thresholdJustCrossed) {
        this._thresholdCrossedSinceOpen = true;
      }

      const hasFreeGift = cartData.items.some((item) => item?.properties?._freeGift === "true");
      if (this._lastFreeGiftInCart === true && !hasFreeGift) {
        this._suppressAutoOpenUntilClose = true;
        this.setLastAutoOpenTs(Date.now());
      }
      this._lastFreeGiftInCart = hasFreeGift;

      if (this._suppressAutoOpenUntilClose) {
        return;
      }
      if (hasFreeGift) {
        return;
      }

      if (cartData.total_price < this.goodieRequirement) {
        return;
      }

      const now = Date.now();
      const cooldownMs = this.autoOpenCooldownSeconds * 1000;
      const lastTs = this.getLastAutoOpenTs();
      const diffMs = now - lastTs;
      // Ignore cooldown if threshold was just crossed in this update
      if (cooldownMs > 0 && !thresholdJustCrossed && lastTs > 0 && diffMs < cooldownMs) {
        return;
      }

      this._autoOpenedInDrawerSession = true;
      this.setLastAutoOpenTs(now);
      this.show();
    }

    checkAndRemoveGift(e) {
      if (!e.cartData?.items) return;
      const freeGoodieProductsInCart = e.cartData?.items.filter((item) => item?.properties?._freeGift === "true");
      if (!freeGoodieProductsInCart || !freeGoodieProductsInCart.length) return;

      const isCartEligibleForFreeGift = e?.cartData?.total_price >= this.goodieRequirement;
      if (isCartEligibleForFreeGift) return;

      this.removeGiftFromCart(freeGoodieProductsInCart);
    }

    async removeGiftFromCart(gifts) {
      const items = Object.fromEntries(gifts.map((item) => [item.key, 0]));
      const url = `${window.Shopify.routes.root}cart/update.js`;
      const request = {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          updates: items,
          sections: this.cart.getSectionsToRender().map((section) => section.section || section.id),
          sections_url: window.location.pathname,
        }),
      };
      try {
        const response = await fetch(url, request);
        const result = await response.json();
        if (!response.ok) {
          publish(PUB_SUB_EVENTS.cartError, {
            source: "product-form",
            errors: result.errors || result.description,
            message: result.message,
          });
          this.handleErrorMessage(result.description);
        } else {
          setTimeout(() => {
            this.rerenderSections(result.sections);
          }, 200);
        }
      } catch (error) {
        console.log(error);
      }
    }

    handleErrorMessage(errorMessage = false) {
      if (this.hideErrors) return;

      this.errorMessageWrapper = this.errorMessageWrapper || this.querySelector(".product-form__error-message-wrapper");
      if (!this.errorMessageWrapper) return;
      this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector(".product-form__error-message");

      this.errorMessageWrapper.toggleAttribute("hidden", !errorMessage);

      if (errorMessage) {
        this.errorMessage.textContent = errorMessage;
      }
    }

    rerenderSections(newData) {
      const sectionsToRender = [
        {
          section: "cart-icon-bubble",
        },
        {
          section: "cart-drawer",
          selector: "#CartDrawer .drawer__header",
        },
        {
          section: "cart-drawer",
          selector: "#CartDrawer #CartDrawer-CartItems",
        },
        {
          section: "cart-drawer",
          selector: "#CartDrawer .cart-drawer__footer",
        },
      ];

      sectionsToRender.forEach((el) => {
        if (!el.selector) {
          const currentContainer = document.querySelector(`#${el.section}`);
          const newContent = this.parseHtml(newData[el.section])?.querySelector(`#shopify-section-${el.section}`)?.innerHTML;
          if (!newContent) return;
          currentContainer.innerHTML = newContent;
        } else {
          const currentContainer = document.querySelector(`${el.selector}`);
          const newContent = this.parseHtml(newData[el.section])?.querySelector(`${el.selector}`)?.innerHTML;
          if (!newContent) return;
          currentContainer.innerHTML = newContent;
        }
      });

      this.removeEmptyAtFirstLoad();
    }

    removeEmptyAtFirstLoad() {
      document.querySelector("cart-drawer")?.classList.remove("is-empty");
      document.querySelector("#CartDrawer cart-drawer-items")?.classList.remove("is-empty");
      document.querySelector("#CartDrawer .drawer__inner-empty")?.remove();
      document.querySelector("#CartDrawer-Checkout").disabled = false;
    }

    parseHtml(string) {
      const parser = new DOMParser();
      const html = parser.parseFromString(string, "text/html");
      return html;
    }

    disconnectedCallback() {
      this.#unsub?.();
      this.#unsub = null;
      document.removeEventListener("cart-drawer:open", this._cartDrawerOpenHandler);
      document.removeEventListener("cart-drawer:close", this._cartDrawerCloseHandler);
      this._closeButton?.removeEventListener("click", this._userDismissHandler);
      this.removeEventListener("keyup", this._userDismissKeyHandler);
      this.removeEventListener("click", this._userDismissClickHandler);
      clearTimeout(this._autoOpenUpdateTimer);
      if (this._quantityObserver) {
        this._quantityObserver.disconnect();
      }
    }
  }

  customElements.define("gift-modal", GiftModal);
}
